// Half-radial gauge — hand-rolled SVG.
// Used for ROI multiple and Mission Outcome Score.

import './charts.css';
import { gaugeGeometry, GAUGE_VIEWBOX } from './gaugeGeometry.js';
import { useOptionalRichTooltipController } from '../richTooltips/RichTooltipProvider.jsx';

export function GaugeCard({
  id,
  value,
  min = 0,
  max = 100,
  label,
  sublabel,
  unit = '',
  tone = 'blue',
  deltaLabel,
  deltaTone = 'sage',
  richTooltip = null,
}) {
  const tooltip = useOptionalRichTooltipController();
  const { x, y, trackPath, filledPath } = gaugeGeometry({ value, min, max });
  const stroke =
    tone === 'sage'
      ? '#5a8c66'
      : tone === 'amber'
        ? '#c08828'
        : tone === 'brick'
          ? '#c0524d'
          : '#1961a1';
  const active = richTooltip && tooltip?.isActive(richTooltip.surfaceId);
  const pinned = richTooltip && tooltip?.isPinned(richTooltip.surfaceId);
  const triggerProps =
    richTooltip && tooltip
      ? tooltip.getTriggerProps({
          surfaceId: richTooltip.surfaceId,
          payload: richTooltip,
          testIdPrefix: 'rich-tooltip',
          ariaLabel: `${label}: ${value}${unit ? unit : ''}`,
        })
      : {};
  const Tag = richTooltip ? 'button' : 'div';

  return (
    <Tag
      type={richTooltip ? 'button' : undefined}
      className={`gauge-card card compact${richTooltip ? ' rich-tooltip-trigger-surface gauge-card-trigger' : ''}${active ? ' is-active' : ''}${pinned ? ' is-pinned' : ''}`}
      data-testid={id ? `gauge-card-${id}` : undefined}
      {...triggerProps}
    >
      <div className="gauge-svg-wrap">
        <svg
          viewBox={`0 0 ${GAUGE_VIEWBOX.width} ${GAUGE_VIEWBOX.height}`}
          width="100%"
          style={{ display: 'block', height: 'auto' }}
          role="img"
          aria-label={`${label}: ${value}`}
        >
          <defs>
            <linearGradient id={`gauge-grad-${tone}`} x1="0" y1="0" x2="1" y2="0">
              <stop offset="0%" stopColor={stroke} stopOpacity={0.2} />
              <stop offset="100%" stopColor={stroke} stopOpacity={1} />
            </linearGradient>
          </defs>
          <path
            d={trackPath}
            fill="none"
            stroke="#c4ccd6"
            strokeWidth="16"
            strokeLinecap="round"
            opacity="0.55"
          />
          <path
            d={filledPath}
            fill="none"
            stroke={`url(#gauge-grad-${tone})`}
            strokeWidth="16"
            strokeLinecap="round"
          />
          <circle cx={x} cy={y} r="7" fill="#fff" stroke={stroke} strokeWidth="3" />
        </svg>
      </div>
      <div className="gauge-text">
        <div className="gauge-value">
          {value}
          <span className="gauge-unit">{unit}</span>
        </div>
        <div className="gauge-label">{label}</div>
        {sublabel ? <div className="gauge-sublabel">{sublabel}</div> : null}
        {deltaLabel ? <span className={`chip ${deltaTone}`}>{deltaLabel}</span> : null}
      </div>
    </Tag>
  );
}
